<template>
  <section class="bg-bg-secondary border border-border-primary rounded-2xl p-5 space-y-4 text-text-primary">
    <div class="flex flex-wrap items-center justify-between gap-3">
      <h2 class="font-bold text-lg">{{ review ? 'Employee OT requests' : 'Overtime requests' }}</h2>
      <div class="flex gap-3">
        <button type="button" class="text-accent-primary" :disabled="busy" @click="refresh">Refresh</button>
        <button v-if="!review" type="button" class="bg-accent-primary text-white px-4 py-2 rounded-lg" @click="showForm = !showForm">Request OT</button>
        <button v-if="review && canCreate" type="button" class="bg-accent-primary text-white px-4 py-2 rounded-lg" @click="showCreate = !showCreate">Create OT</button>
      </div>
    </div>
    <p v-if="!review" class="text-sm text-text-secondary">Request at least 5 hours before your original shift ends. Only approved OT extends your shift. Automatic clock-out is 1 hour after the shift ends, including approved OT.</p>
    <p v-else-if="canCreate" class="text-sm text-text-secondary">Approved OT is paid on the payslip. A public-holiday roster pays as Rest OT. Other days pay as Normal OT at 1.5×.</p>
    <p v-if="error" role="alert" class="text-danger">{{ error }}</p>
    <form v-if="showForm && !review" class="space-y-3" @submit.prevent="submit">
      <label class="block">Roster slot
        <select v-model="rosterId" required class="block w-full p-3 rounded-lg bg-bg-primary border border-border-primary">
          <option value="" disabled>Select a shift</option>
          <option v-for="slot in rosters" :key="slot.id" :value="slot.id" :disabled="!eligible(slot)">{{ slot.date }} · {{ slot.startTime }}–{{ slot.endTime }}{{ eligible(slot) ? '' : ' (deadline passed or already requested)' }}</option>
        </select>
      </label>
      <p v-if="selected" class="text-sm text-text-secondary">Request deadline: {{ format(selected.end - 5 * 3600000) }}</p>
      <label class="block">OT hours
        <input v-model.number="hours" required type="number" min="0.25" max="12" step="0.25" class="block w-full p-3 rounded-lg bg-bg-primary border border-border-primary" />
      </label>
      <label class="block">Reason
        <textarea v-model="reason" required maxlength="2000" rows="3" class="block w-full p-3 rounded-lg bg-bg-primary border border-border-primary"></textarea>
      </label>
      <button :disabled="busy || !selected || !eligible(selected) || !reason.trim()" class="bg-accent-primary text-white px-4 py-2 rounded-lg disabled:opacity-50">Submit OT request</button>
    </form>
    <form v-if="showCreate && review && canCreate" class="space-y-3" @submit.prevent="assign">
      <label class="block">Employee
        <select v-model="employeeId" required class="block w-full p-3 rounded-lg bg-bg-primary border border-border-primary" @change="loadRosterOptions">
          <option value="" disabled>Select an employee</option>
          <option v-for="person in employees" :key="person.id" :value="person.id">{{ person.name }}</option>
        </select>
      </label>
      <label class="block">Roster slot
        <select v-model="rosterId" required class="block w-full p-3 rounded-lg bg-bg-primary border border-border-primary">
          <option value="" disabled>Select a shift</option>
          <option v-for="slot in assignRosters" :key="slot.id" :value="slot.id">{{ slot.date }} · {{ slot.startTime }}–{{ slot.endTime }}{{ slot.publicHoliday ? ' · Public holiday' : '' }}</option>
        </select>
      </label>
      <p v-if="selectedAssign?.publicHoliday" class="text-sm text-amber-600">This date is a public holiday. These hours are added to Rest OT for that date, on top of any hours already clocked in.</p>
      <label class="block">OT hours
        <input v-model.number="hours" required type="number" min="0.25" max="12" step="0.25" class="block w-full p-3 rounded-lg bg-bg-primary border border-border-primary" />
      </label>
      <label class="block">Reason
        <textarea v-model="reason" required maxlength="2000" rows="3" class="block w-full p-3 rounded-lg bg-bg-primary border border-border-primary"></textarea>
      </label>
      <button :disabled="busy || !employeeId || !rosterId || !reason.trim()" class="bg-accent-primary text-white px-4 py-2 rounded-lg disabled:opacity-50">Create approved OT</button>
    </form>
    <p v-if="!requests.length && !busy" class="text-text-secondary text-sm">No OT requests yet.</p>
    <article v-for="request in listedRequests" :key="request.id" class="border border-border-primary rounded-xl p-4 space-y-2">
      <div class="flex flex-wrap justify-between gap-2">
        <strong>{{ review ? `${request.employeeName} · ${request.date}` : request.date }} · {{ request.minutes / 60 }} hours {{ request.payKind === 'public_holiday' || request.publicHoliday ? 'public holiday OT' : 'OT' }}</strong>
        <span class="font-semibold capitalize" :class="request.status === 'approved' ? 'text-success' : request.status === 'rejected' ? 'text-danger' : 'text-text-secondary'">{{ request.status }}</span>
      </div>
      <p class="text-sm">Regular shift: {{ format(request.originalStart) }} – {{ format(request.originalEnd) }}</p>
      <p class="text-sm">{{ request.status === 'approved' ? 'Approved OT' : 'Requested OT' }}: {{ format(request.originalEnd) }} – {{ format(request.requestedEnd) }}</p>
      <p v-if="request.status === 'approved'" class="text-sm font-medium">Working shift: {{ format(request.originalStart) }} – {{ format(request.requestedEnd) }}. Auto clock-out: {{ format(Date.parse(request.requestedEnd) + 3600000) }}.</p>
      <p class="text-sm whitespace-pre-wrap">{{ request.reason }}</p>
      <div v-if="review && request.status === 'pending'" class="flex gap-4">
        <button :disabled="busy" type="button" class="text-success font-semibold" @click="decide(request, 'approved')">Approve</button>
        <button :disabled="busy" type="button" class="text-danger font-semibold" @click="decide(request, 'rejected')">Reject</button>
      </div>
    </article>
    <button
      v-if="review && approvedRequests.length"
      type="button"
      class="text-accent-primary font-semibold text-sm"
      @click="showApproved = !showApproved"
    >
      {{ showApproved ? 'See less' : 'See more' }}
    </button>
  </section>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { apiGet, apiPost } from '@/services/apiClient'
const props = defineProps({ review: Boolean })
const requests = ref([])
const rosters = ref([])
const employees = ref([])
const assignRosters = ref([])
const canCreate = ref(false)
const showForm = ref(false)
const showCreate = ref(false)
const busy = ref(false)
const error = ref('')
const rosterId = ref('')
const employeeId = ref('')
const hours = ref(1)
const reason = ref('')
const now = ref(Date.now())
const selected = computed(() => rosters.value.find(slot => slot.id === rosterId.value))
const selectedAssign = computed(() => assignRosters.value.find(slot => slot.id === rosterId.value))
const showApproved = ref(false)
const approvedRequests = computed(() => requests.value.filter(request => String(request.status || '').toLowerCase() === 'approved'))
const listedRequests = computed(() => {
  if (!props.review) return requests.value
  const pending = requests.value.filter(request => String(request.status || '').toLowerCase() === 'pending')
  return showApproved.value ? [...pending, ...approvedRequests.value] : pending
})
const format = value => new Date(value).toLocaleString('en-MY', { timeZone: 'Asia/Kuala_Lumpur', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })
const eligible = slot => now.value <= slot.end - 5 * 3600000 && !requests.value.some(request => request.rosterId === slot.id)
const message = err => err.response?.data?.error || err.message
async function refresh() {
  try {
    const data = await apiGet(`/v1/attendance-actions/overtime${props.review ? '?review=true' : ''}`)
    requests.value = data.requests
    rosters.value = data.rosters
    employees.value = data.employees || []
    canCreate.value = data.canCreate === true
    error.value = ''
  } catch (err) { error.value = message(err) }
}
async function loadRosterOptions() {
  assignRosters.value = []
  rosterId.value = ''
  if (!employeeId.value) return
  try {
    const data = await apiGet(`/v1/attendance-actions/overtime/options?employeeId=${encodeURIComponent(employeeId.value)}`)
    assignRosters.value = data.rosters || []
  } catch (err) { error.value = message(err) }
}
async function assign() {
  busy.value = true
  try {
    await apiPost('/v1/attendance-actions/overtime/assign', {
      employeeId: employeeId.value,
      rosterId: rosterId.value,
      minutes: Math.round(hours.value * 60),
      reason: reason.value,
    })
    showCreate.value = false
    reason.value = ''
    rosterId.value = ''
    await refresh()
  } catch (err) { error.value = message(err) } finally { busy.value = false }
}
async function submit() {
  busy.value = true
  try {
    await apiPost('/v1/attendance-actions/overtime', { rosterId: rosterId.value, minutes: Math.round(hours.value * 60), reason: reason.value })
    showForm.value = false
    reason.value = ''
    rosterId.value = ''
    await refresh()
  } catch (err) { error.value = message(err) } finally { busy.value = false }
}
async function decide(request, status) {
  busy.value = true
  try {
    await apiPost(`/v1/attendance-actions/overtime/${request.id}/decision`, { status })
    await refresh()
  } catch (err) { error.value = message(err) } finally { busy.value = false }
}
let timer
const visibleRefresh = () => { if (document.visibilityState === 'visible') { now.value = Date.now(); void refresh() } }
onMounted(() => {
  void refresh()
  timer = setInterval(() => { now.value = Date.now(); if (!busy.value && document.visibilityState === 'visible') void refresh() }, 60000)
  document.addEventListener('visibilitychange', visibleRefresh)
})
onUnmounted(() => { clearInterval(timer); document.removeEventListener('visibilitychange', visibleRefresh) })
</script>
